import type { CrosswordDoc } from './doc';
import { deriveIndices } from './deriveIndices';

// Projeta o modelo autoral numa grade para o Canvas desenhar. Espelha o que o
// `gera_matrix` do servidor produz, mas só o que o EDITOR precisa mostrar — e a partir
// do modelo, não de engenharia reversa de matriz.
//
// Semântica de renderização espelhada do player real (app-recreativa/features/
// crosswords): a casa morta mostra a(s) dica(s) concatenadas com " / " (ordem slot1,
// slot2); o número da palavra aparece na sua casa INICIAL; letra compartilhada por
// duas palavras é um cruzamento (conflito quando as letras discordam).

export interface ProjectedCell {
  x: number;
  y: number;
  kind: 'dead' | 'letter' | 'empty';
  letter: string; // só em 'letter'
  numbers: number[]; // números das palavras que COMEÇAM aqui (pode haver duas)
  clue: string; // só em 'dead': as dicas dos slots juntas com " / "
  conflict: boolean; // 'letter' com letras de cruzamento discordando
}

export type ProjectedGrid = ProjectedCell[][];

export function projectGrid(doc: CrosswordDoc): ProjectedGrid {
  const grid: ProjectedGrid = [];
  for (let y = 0; y < doc.altura; y++) {
    const row: ProjectedCell[] = [];
    for (let x = 0; x < doc.largura; x++) {
      row.push({ x, y, kind: 'empty', letter: '', numbers: [], clue: '', conflict: false });
    }
    grid.push(row);
  }

  const inGrid = (x: number, y: number) => y >= 0 && y < doc.altura && x >= 0 && x < doc.largura;
  const wordByRef = new Map(doc.words.map((w) => [w.ref, w]));
  const indices = deriveIndices(doc);

  // Casas mortas: dica = chaves dos slots (ordem slot1, slot2) juntas com " / ".
  for (const c of doc.deadCells) {
    if (!inGrid(c.x, c.y)) continue;
    const clues: string[] = [];
    for (const ref of [c.palavra1Ref, c.palavra2Ref]) {
      const chave = ref != null ? wordByRef.get(ref)?.chave.trim() : '';
      if (chave) clues.push(chave);
    }
    const cell = grid[c.y][c.x];
    cell.kind = 'dead';
    cell.clue = clues.join(' / ');
  }

  // Letras das palavras; número na casa inicial; conflito quando duas discordam.
  for (const w of doc.words) {
    for (let i = 0; i < w.texto.length; i++) {
      const x = w.direcao ? w.x : w.x + i;
      const y = w.direcao ? w.y + i : w.y;
      if (!inGrid(x, y)) continue;
      const cell = grid[y][x];
      const letter = w.texto[i];
      if (cell.kind === 'letter' && cell.letter !== letter) cell.conflict = true;
      cell.kind = 'letter';
      cell.letter = cell.conflict ? cell.letter : letter;
      if (i === 0) {
        const n = indices[w.ref];
        if (n != null) cell.numbers.push(n);
      }
    }
  }

  return grid;
}
